// src/pages/QRPrintPage.tsx — Real, scannable QR generation + printing
import React, { useState, useEffect, useCallback } from 'react';
import QRCode from 'qrcode';
import { getBags, getCompanies } from '../services/api';
import type { QueryParams } from '../services/api';
import type { Bag, Company } from '../types';

export default function QRPrintPage() {
  const [bags,      setBags]      = useState<Bag[]>([]);
  const [qrMap,     setQrMap]     = useState<Record<string, string>>({});   // bagId -> QR dataURL
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState('');
  const [status,    setStatus]    = useState('PENDING');
  const [loading,   setLoading]   = useState(true);

  // Companies for the filter dropdown
  useEffect(() => {
    getCompanies().then(r => setCompanies(r.data.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: QueryParams = { limit: 60 };
      if (companyId) params.company_id = companyId;
      if (status)    params.status     = status;

      const r    = await getBags(params);
      const list = r.data.data || [];
      setBags(list);

      // Generate a REAL scannable QR for each bag.
      // Encode the CURRENT host's URL so the QR works wherever the app is
      // opened from — localhost, LAN IP (phone testing), or production domain.
      const entries = await Promise.all(list.map(async (b): Promise<[string, string]> => {
        const url     = `${window.location.origin}/scan/${b.bagId}`;
        const dataUrl = await QRCode.toDataURL(url, {
          width: 200, margin: 1,
          color: { dark: '#072E20', light: '#FFFFFF' },
          errorCorrectionLevel: 'M',
        });
        return [b.bagId, dataUrl];
      }));
      setQrMap(Object.fromEntries(entries));
    } catch {
      setBags([]); setQrMap({});
    } finally {
      setLoading(false);
    }
  }, [companyId, status]);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">🖨️ QR Print Manager</div>
        <span style={{ fontSize:11, color:'var(--ink3)' }}>Real, scannable QR codes</span>
      </div>

      <div className="page-content fade-in">

        {/* Controls — hidden when printing */}
        <div className="card no-print" style={{ marginBottom:16 }}>
          <div className="card-title">Generate &amp; print QR stickers</div>
          <div style={{ display:'flex', gap:12, alignItems:'flex-end', flexWrap:'wrap' }}>
            <div className="field" style={{ marginBottom:0, minWidth:190 }}>
              <label>Company</label>
              <select value={companyId} onChange={e => setCompanyId(e.target.value)}>
                <option value="">All companies</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
              </select>
            </div>
            <div className="field" style={{ marginBottom:0, minWidth:160 }}>
              <label>Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)}>
                <option value="">All</option>
                <option value="PENDING">Pending (need print)</option>
                <option value="IN_TRANSIT">In Transit</option>
                <option value="AT_RETAILER">At Retailer</option>
                <option value="DELIVERED">Delivered</option>
              </select>
            </div>
            <button className="btn btn-secondary" onClick={load} disabled={loading}>
              {loading ? <span className="spin">⟳</span> : '↻'} Reload
            </button>
            <button
              className="btn btn-primary"
              onClick={() => window.print()}
              disabled={!bags.length || loading}
              style={{ marginLeft:'auto' }}
            >
              🖨️ Print {bags.length ? `(${bags.length})` : ''}
            </button>
          </div>
          <div style={{ marginTop:12, fontSize:11, color:'var(--ink3)', lineHeight:1.6 }}>
            Each QR encodes the bag's verification URL — a farmer scanning it opens the registration page.
            Showing up to 60 bags; use the filters to narrow down a batch.
          </div>
        </div>

        {/* QR sheet — this is what gets printed */}
        {loading ? (
          <div className="card" style={{ textAlign:'center', padding:40 }}>
            <span className="spin">⟳</span> Generating QR codes…
          </div>
        ) : bags.length === 0 ? (
          <div className="card" style={{ textAlign:'center', padding:40, color:'var(--ink3)' }}>
            No bags found for this filter. Upload bags first, then print their QR codes here.
          </div>
        ) : (
          <div className="qr-grid">
            {bags.map(b => (
              <div key={b.bagId} className="qr-card">
                {qrMap[b.bagId]
                  ? <img src={qrMap[b.bagId]} alt={b.bagId} className="qr-img" />
                  : <div className="qr-img" style={{ background:'var(--bg)' }} />}
                <div className="qr-id">{b.bagId}</div>
                <div className="qr-meta">{b.weightKg}kg · {b.company?.code || '—'}</div>
                <div className="qr-gov">Government of India · Fertilizer Tracking</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
